import { differenceInDays } from "date-fns";

import { parsedDate } from "../utils/dates";
import { may } from "./";
import {
  customerFeatureEnabled,
  featureEnabled,
  isAdmin,
  isCustomerAdmin,
  isRoot,
  isStaffAdmin,
  projectFeatureEnabled,
  userAndTargetAreAdmin,
  userFeatureEnabled,
} from "./helpers";

type EmployeePermissionResource = EmployeeRef & {
  userId: string;
  user: UserRef & { role: UserRole };
};

type ChangeRequestPermissionResource = {
  employeeId: Nullable<string>;
  status: import("@/gql/graphql").ChangeRequestStatus;
  employee: Nullable<EmployeeRef & { userId: string }>;
};

type WorktimePermissionResource = Pick<WorktimeInterface, "userId">;

export interface StaffMayInterface {
  // staff/worktimes
  (session: SessionInterface, path: "staff/worktimes"): boolean;
  (session: SessionInterface, path: "staff/worktimes", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "staff/worktimes",
    action: "show" | "edit" | "delete",
    wt: WorktimePermissionResource,
  ): boolean;

  // staff/worktime-change-requests
  (session: SessionInterface, path: "staff/worktime-change-requests"): boolean;
  (session: SessionInterface, path: "staff/worktime-change-requests", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "staff/worktime-change-requests",
    action: "show" | "edit" | "delete" | "approve" | "reject" | "request",
    request: WorktimeChangeRequestType,
  ): boolean;

  // staff/timesheets
  (session: SessionInterface, path: "staff/timesheets"): boolean;
  (session: SessionInterface, path: "staff/timesheets", action: "show", user: UserRef): boolean;
  (session: SessionInterface, path: "staff/timesheets", action: "absenceOverview"): boolean;

  // staff/employees
  (session: SessionInterface, path: "staff/employees"): boolean;
  (session: SessionInterface, path: "staff/employees", action: "new" | "archive"): boolean;
  (
    session: SessionInterface,
    path: "staff/employees",
    action:
      | "show"
      | "edit"
      | "delete"
      | "contracts"
      | "files"
      | "internal"
      | "settlements"
      | "residualLeaves"
      | "send"
      | "address"
      | "sensitiveProfileInfos"
      | "sensitiveEmployeeInfos",
    employee: EmployeePermissionResource,
  ): boolean;

  // staff/file-types
  (session: SessionInterface, path: "staff/file-types"): boolean;
  (session: SessionInterface, path: "staff/file-types", action: "new" | "missing"): boolean;
  (
    session: SessionInterface,
    path: "staff/file-types",
    action: "edit" | "delete",
    type: PersonalFileTypeDetail,
  ): boolean;

  // staff/file-types
  (
    session: SessionInterface,
    path: "staff/contracts",
    action: "edit" | "delete" | "genContract" | "genContractAdaption",
    contrac: ContractDetail,
  ): boolean;

  // staff/histories
  (session: SessionInterface, path: "staff/histories"): boolean;
  (session: SessionInterface, path: "staff/histories", action: "new", employee: EmployeePermissionResource): boolean;
  (
    session: SessionInterface,
    path: "staff/histories",
    action: "edit" | "delete",
    history: StaffHistoryInterface,
  ): boolean;

  // staff/files
  (
    session: SessionInterface,
    path: "staff/files",
    action: "edit" | "delete" | "send" | "sendExchange" | "signature",
    file: PersonalFileDetail,
  ): boolean;

  // staff/vacation_requests
  (session: SessionInterface, path: "staff/vacation_requests"): boolean;
  (session: SessionInterface, path: "staff/vacation_requests", action: "new" | "plan" | "overview"): boolean;
  (
    session: SessionInterface,
    path: "staff/vacation_requests",
    action: "edit" | "delete" | "request" | "finalize",
    request: VacationRequestDetail,
  ): boolean;

  // staff/presettlements
  (session: SessionInterface, path: "staff/presettlements"): boolean;
  (session: SessionInterface, path: "staff/presettlements", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "staff/presettlements",
    action: "show" | "edit" | "delete",
    presettlement: PresettlementRef,
  ): boolean;

  // staff/settlements
  (session: SessionInterface, path: "staff/settlements"): boolean;
  (session: SessionInterface, path: "staff/settlements", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "staff/settlements",
    action: "edit" | "delete" | "download" | "finalize",
    settlement: SettlementPermissionResource,
  ): boolean;

  // staff/residual_leaves
  (session: SessionInterface, path: "staff/residual_leaves", action: "edit" | "delete"): boolean;

  // staff/company-news
  (session: SessionInterface, path: "staff/company-news"): boolean;
  (session: SessionInterface, path: "staff/company-news", action: "new"): boolean;
  (session: SessionInterface, path: "staff/company-news", action: "edit" | "delete"): boolean;

  // staff/coachings
  (session: SessionInterface, path: "staff/coachings"): boolean;
  (session: SessionInterface, path: "staff/coachings", action: "new" | "delete" | "edit"): boolean;
  (session: SessionInterface, path: "staff/coachings", action: "show" | "confirm", coaching: CoachingDetail): boolean;

  // staff/coupons
  (session: SessionInterface, path: "staff/coupons"): boolean;
  (session: SessionInterface, path: "staff/coupons", action: "new"): boolean;
  (session: SessionInterface, path: "staff/coupons", action: "edit" | "delete", coupon: EmployeeCouponRef): boolean;

  // staff/performances
  (session: SessionInterface, path: "staff/performance"): boolean;
  (session: SessionInterface, path: "staff/performance", action: "show", user: UserRef): boolean;

  // staff
  (session: SessionInterface, path: "staff"): boolean;
  (session: SessionInterface, path: "staff", action: "supervisor"): boolean;
  (session: SessionInterface, path: "staff", action: "controlling"): boolean;

  // change requests
  (session: SessionInterface, path: "staff/change-requests"): boolean;
  (session: SessionInterface, path: "staff/change-requests", action: "new" | "send"): boolean;
  (
    session: SessionInterface,
    path: "staff/change-requests",
    action: "edit" | "show" | "delete" | "request" | "return" | "reject" | "approve" | "create-user",
    coaching: ChangeRequestPermissionResource,
  ): boolean;

  // worktimes-customer-review
  (session: SessionInterface, path: "staff/worktimes-customer-review"): boolean;
  (session: SessionInterface, path: "staff/worktimes-customer-review", action: "show"): boolean;

  // productive hour goals
  (session: SessionInterface, path: "staff/productive-hour-goals"): boolean;
  (session: SessionInterface, path: "staff/productive-hour-goals", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "staff/productive-hour-goals",
    action: "edit",
    goal: ProductiveHourGoalDetail,
  ): boolean;

  // sick notes
  (session: SessionInterface, path: "staff/sick-notes"): boolean;
  (session: SessionInterface, path: "staff/sick-notes", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "staff/sick-notes",
    action: "show" | "edit" | "delete" | "addCert" | "confirm" | "decline",
    sickNote: SickNoteDetail,
  ): boolean;

  // late notes
  (session: SessionInterface, path: "staff/late-notes"): boolean;
  (session: SessionInterface, path: "staff/late-notes", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "staff/late-notes",
    action: "show" | "edit" | "delete",
    sickNote: LateNoteDetail,
  ): boolean;

  // payroll PDF imports
  (session: SessionInterface, path: "staff/payroll-pdf-imports"): boolean;
  (session: SessionInterface, path: "staff/payroll-pdf-imports", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "staff/payroll-pdf-imports",
    action: "show" | "edit" | "delete",
    pdfImport: PayrollPdfImport,
  ): boolean;

  // payment infos
  (session: SessionInterface, path: "staff/payment-infos"): boolean;
  (session: SessionInterface, path: "staff/payment-infos", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "staff/payment-infos",
    action: "show" | "edit" | "delete",
    pdfImport: StaffPaymentInfoRef,
  ): boolean;

  // payment reports
  (session: SessionInterface, path: "staff/payment-reports"): boolean;
  (session: SessionInterface, path: "staff/payment-reports", action: "payrollDeviations" | "financeReports"): boolean;

  // profile
  (session: SessionInterface, path: "staff/profile"): boolean;
  (session: SessionInterface, path: "staff/profile", action: "user-specific-start-project"): boolean;
}

const WORKTIME_PERMISSIONS: PermissionType<WorktimePermissionResource> = {
  "staff/worktimes": {
    index: ({ currentUser, currentCustomer }) => featureEnabled(currentUser, currentCustomer, "worktimes"),
    edit: ({ currentCustomer, currentUser }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) || (currentUser.teamLeader && resource.userId !== currentUser.id)),
    delete: ({ currentCustomer, currentUser }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) || (currentUser.teamLeader && resource.userId !== currentUser.id)),
    new: ({ currentCustomer, currentUser }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && (isRoot(currentUser) || currentUser.teamLeader),
  },
};

export const WORKTIME_CHANGE_REQUEST_PERMISSION: PermissionType<WorktimeChangeRequestType> = {
  "staff/worktime-change-requests": {
    index: ({ currentUser, currentCustomer }) => featureEnabled(currentUser, currentCustomer, "worktimes"),
    delete: ({ currentCustomer, currentUser }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) ||
        isStaffAdmin(currentUser) ||
        currentUser.teamLeader ||
        currentUser.id === resource.userId) &&
      !["APPROVED", "REJECTED"].includes(resource.status),
    edit: ({ currentCustomer, currentUser }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) ||
        isStaffAdmin(currentUser) ||
        currentUser.teamLeader ||
        currentUser.id === resource.userId) &&
      !["APPROVED", "REJECTED"].includes(resource.status),
    approve: ({ currentCustomer, currentUser }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) || isStaffAdmin(currentUser) || currentUser.teamLeader) &&
      !["APPROVED", "REJECTED"].includes(resource.status),
    reject: ({ currentCustomer, currentUser }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) || isStaffAdmin(currentUser) || currentUser.teamLeader) &&
      !["APPROVED", "REJECTED"].includes(resource.status),
    request: ({ currentCustomer, currentUser }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) || isStaffAdmin(currentUser) || currentUser.teamLeader) &&
      !["APPROVED", "REJECTED"].includes(resource.status),
  },
};

const TIMESHEET_PERMISSIONS: PermissionType<EmployeePermissionResource> = {
  "staff/timesheets": {
    index: ({ currentUser, currentCustomer }) => featureEnabled(currentUser, currentCustomer, "worktimes"),
    show: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) || resource.id === currentUser.id),
    absenceOverview: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isStaffAdmin(currentUser) || currentUser.teamLeader),
  },
};

const EMPLOYEE_PERMISSIONS: PermissionType<EmployeePermissionResource> = {
  "staff/employees": {
    index: ({ currentUser, currentCustomer }, _resource) => featureEnabled(currentUser, currentCustomer, "worktimes"),
    delete: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
    new: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isStaffAdmin(currentUser),
    archive: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isStaffAdmin(currentUser),
    edit: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isStaffAdmin(currentUser),
    send: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isStaffAdmin(currentUser),
    contracts: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      ((isStaffAdmin(currentUser) && !userAndTargetAreAdmin(currentUser, resource.user)) ||
        currentUser.id === resource.userId),
    files: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isStaffAdmin(currentUser) || currentUser.id === resource.userId),
    internal: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      ((isStaffAdmin(currentUser) && !userAndTargetAreAdmin(currentUser, resource.user)) ||
        currentUser.id === resource.userId),
    settlements: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      ((isStaffAdmin(currentUser) && !userAndTargetAreAdmin(currentUser, resource.user)) ||
        currentUser.id === resource.userId),
    sensitiveProfileInfos: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) || currentUser.id === resource.userId),
    sensitiveEmployeeInfos: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) || currentUser.id === resource.userId),
    address: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isAdmin(currentUser) || currentUser.id === resource.userId),
  },
};

const FILE_TYPES_PERMISSIONS: PermissionType<PersonalFileTypeDetail> = {
  "staff/file-types": {
    index: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
  },
};

const CONTRACTS_PERMISSIONS: PermissionType<ContractDetail> = {
  "staff/contracts": {
    edit: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isStaffAdmin(currentUser),
    delete: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
    genContract: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isStaffAdmin(currentUser),
    genContractAdaption: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isStaffAdmin(currentUser),
  },
};

const STAFF_HISTORIES_PERMISSIONS: PermissionType<StaffHistoryInterface> = {
  "staff/histories": {
    index: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isCustomerAdmin(currentUser, currentCustomer),
    new: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isCustomerAdmin(currentUser, currentCustomer),
    edit: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isCustomerAdmin(currentUser, currentCustomer),
    delete: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isCustomerAdmin(currentUser, currentCustomer),
  },
};

const FILES_PERMISSIONS: PermissionType<PersonalFileDetail> = {
  "staff/files": {
    edit: ({ currentUser, currentCustomer }, _file) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
    send: ({ currentUser, currentCustomer }, file) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      isRoot(currentUser) &&
      file.fileType?.category === "contract",
    sendExchange: ({ currentUser, currentCustomer }, _file) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
    signature: ({ currentUser }, file) => isRoot(currentUser) && file.fileType?.category === "contract_adaption",
    delete: ({ currentUser, currentCustomer }, _file) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
  },
};

const VACATION_PERMISSIONS: PermissionType<VacationRequestDetail> = {
  "staff/vacation_requests": {
    index: ({ currentUser, currentCustomer }) => featureEnabled(currentUser, currentCustomer, "worktimes"),
    delete: ({ currentUser, currentCustomer }, request) =>
      ["AWAITING", "QUERY"].includes(request.status) &&
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      isRoot(currentUser),
    edit: ({ currentUser, currentCustomer }, request) =>
      ["AWAITING", "QUERY"].includes(request.status) &&
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (request.userId === currentUser.id || isRoot(currentUser)),
    request: ({ currentUser, currentCustomer }, request) =>
      ["AWAITING", "QUERY"].includes(request.status) && featureEnabled(currentUser, currentCustomer, "worktimes"),
    finalize: ({ currentUser, currentCustomer }, request) =>
      ["AWAITING", "QUERY"].includes(request.status) &&
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) || request.userId !== currentUser.id),
    plan: ({ currentUser, currentCustomer }, _) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isStaffAdmin(currentUser) || currentUser.teamLeader),
  },
};

const PRESETTLEMENT_PERMISSIONS: PermissionType<PresettlementRef> = {
  "staff/presettlements": {
    index: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
  },
};

const MAX_EDITABLE_DAYS = 7;

const SETTLEMENT_PERMISSIONS: PermissionType<SettlementPermissionResource> = {
  "staff/settlements": {
    index: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
    edit: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      isRoot(currentUser) &&
      (!resource.isVisible ||
        differenceInDays(new Date(), parsedDate(resource.visibleAt || resource.insertedAt)) <= MAX_EDITABLE_DAYS),
    delete: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      isRoot(currentUser) &&
      (!resource.isVisible ||
        differenceInDays(new Date(), parsedDate(resource.visibleAt || resource.insertedAt)) <= MAX_EDITABLE_DAYS),
    finalize: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      isRoot(currentUser) &&
      (!resource.isVisible ||
        differenceInDays(new Date(), parsedDate(resource.visibleAt || resource.insertedAt)) <= MAX_EDITABLE_DAYS),
  },
};

const RESIDUAL_LEAVES_PERMISSIONS: PermissionType<ResidualLeaveDetail> = {
  "staff/residual_leaves": {
    edit: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
    delete: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
  },
};

const COMPANY_NEWS_PREMISSIONS: PermissionType<CompanyNewsPostRef> = {
  "staff/company-news": {
    index: ({ currentUser, currentCustomer }) => featureEnabled(currentUser, currentCustomer, "company_news"),
    new: ({ currentUser }) => isStaffAdmin(currentUser),
    edit: ({ currentUser }) => isRoot(currentUser),
  },
};

const COACHING_PREMISSIONS: PermissionType<CoachingDetail> = {
  "staff/coachings": {
    index: ({ currentUser, currentCustomer }) =>
      (isRoot(currentUser) && projectFeatureEnabled(currentCustomer, "coachings")) ||
      featureEnabled(currentUser, currentCustomer, "coachings"),
    new: ({ currentUser, currentCustomer }) =>
      (isRoot(currentUser) && projectFeatureEnabled(currentCustomer, "coachings")) ||
      (featureEnabled(currentUser, currentCustomer, "coachings") && currentUser.teamLeader),
    edit: ({ currentCustomer, currentUser }) =>
      (isRoot(currentUser) && projectFeatureEnabled(currentCustomer, "coachings")) ||
      (featureEnabled(currentUser, currentCustomer, "coachings") && currentUser.teamLeader),
    show: ({ currentCustomer, currentUser }, resource) =>
      (isRoot(currentUser) && projectFeatureEnabled(currentCustomer, "coachings")) ||
      (featureEnabled(currentUser, currentCustomer, "coachings") &&
        (currentUser.teamLeader || resource.user.id === currentUser.id)),
    confirm: ({ currentCustomer, currentUser }, resource) =>
      featureEnabled(currentUser, currentCustomer, "coachings") &&
      !resource.signed &&
      resource.user.id === currentUser.id,
    delete: ({ currentCustomer, currentUser }) =>
      (isRoot(currentUser) && projectFeatureEnabled(currentCustomer, "coachings")) ||
      (featureEnabled(currentUser, currentCustomer, "coachings") && currentUser.teamLeader),
  },
};

const COUPON_PERMISSIONS: PermissionType<EmployeeCouponRef> = {
  "staff/coupons": {
    index: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isStaffAdmin(currentUser),
    new: ({ currentUser }, _resource) => isStaffAdmin(currentUser),
    edit: ({ currentUser }, _resource) => isStaffAdmin(currentUser),
    delete: ({ currentUser }, _resource) => isStaffAdmin(currentUser),
  },
};

const PERFORMANCE_PERMISSIONS: PermissionType<EmployeePermissionResource> = {
  "staff/performance": {
    index: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isStaffAdmin(currentUser),
    show: ({ currentUser, currentCustomer }, user) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isStaffAdmin(currentUser) || currentUser.id === user.id || currentUser.teamLeader),
  },
};

const CHANGE_REQUEST_PERMISSIONS: PermissionType<ChangeRequestPermissionResource> = {
  "staff/change-requests": {
    index: ({ currentUser, currentCustomer }, _resource) => featureEnabled(currentUser, currentCustomer, "worktimes"),
    show: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (resource.employee?.userId === currentUser.id || isStaffAdmin(currentUser)),
    return: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      currentUser.id === resource.employee?.userId &&
      resource.status === "REQUEST",
    request: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      isStaffAdmin(currentUser) &&
      !["APPROVED", "REJECTED", "REQUEST"].includes(resource.status) &&
      !!resource.employeeId,
    edit: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      isStaffAdmin(currentUser) &&
      !["APPROVED", "REJECTED"].includes(resource.status),
    delete: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isStaffAdmin(currentUser),
    reject: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      isStaffAdmin(currentUser) &&
      !["APPROVED", "REJECTED"].includes(resource.status) &&
      !!resource.employeeId,
    approve: ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      isStaffAdmin(currentUser) &&
      !["APPROVED", "REJECTED"].includes(resource.status) &&
      !!resource.employeeId,
    "create-user": ({ currentUser, currentCustomer }, resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      isRoot(currentUser) &&
      !["APPROVED", "REJECTED"].includes(resource.status) &&
      !resource.employeeId,
    send: ({ currentUser, currentCustomer }, _resource) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isStaffAdmin(currentUser),
  },
};

const WORKTIME_REVIEW_PERMISSIONS: PermissionType<EmployeePermissionResource> = {
  "staff/worktimes-customer-review": {
    index: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isStaffAdmin(currentUser),
    show: ({ currentUser, currentCustomer }, _user) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isStaffAdmin(currentUser) || currentUser.teamLeader),
  },
};

const PRODUCTIVE_HOUR_GOAL_PERMISSIONS: PermissionType<ProductiveHourGoalDetail> = {
  "staff/productive-hour-goals": {
    index: ({ currentUser, currentCustomer }) =>
      customerFeatureEnabled(currentCustomer, "worktimes") &&
      (isAdmin(currentUser) || userFeatureEnabled(currentUser, "productive-hour-goals")),
    new: ({ currentUser, currentCustomer }) =>
      customerFeatureEnabled(currentCustomer, "worktimes") &&
      (isAdmin(currentUser) || userFeatureEnabled(currentUser, "productive-hour-goals")),
    edit: ({ currentUser, currentCustomer }) =>
      customerFeatureEnabled(currentCustomer, "worktimes") &&
      (isAdmin(currentUser) || userFeatureEnabled(currentUser, "productive-hour-goals")),
  },
};

const SICK_NOTE_PERMISSIONS: PermissionType<SickNoteDetail> = {
  "staff/sick-notes": {
    index: ({ currentUser, currentCustomer }) => featureEnabled(currentUser, currentCustomer, "worktimes"),
    edit: ({ currentUser, currentCustomer }, _note) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
    delete: ({ currentUser, currentCustomer }, _note) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
    addCert: ({ currentUser, currentCustomer }, sickNote) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) ||
        (sickNote.userId === currentUser.id &&
          ["NEW", "CERTIFICATE_WANTED", "CERTIFICATE_GIVEN"].includes(sickNote.status))),
    confirm: ({ currentUser, currentCustomer }, note) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      isRoot(currentUser) &&
      note.status !== "CONFIRMED" &&
      note.status !== "DECLINED" &&
      ((note.status === "CERTIFICATE_WANTED" && note.hasSickCertificate) || note.status !== "CERTIFICATE_WANTED"),
    decline: ({ currentUser, currentCustomer }, note) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      isRoot(currentUser) &&
      note.status !== "CONFIRMED" &&
      note.status !== "DECLINED",
  },
};

const LATE_NOTE_PERMISSIONS: PermissionType<LateNoteDetail> = {
  "staff/late-notes": {
    index: ({ currentUser, currentCustomer }) => featureEnabled(currentUser, currentCustomer, "worktimes"),
    edit: ({ currentUser, currentCustomer }, note) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      (isRoot(currentUser) || note.userId === currentUser.id),
    delete: ({ currentUser, currentCustomer }, _note) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
  },
};

const PAYROLL_PDF_IMPORT_PERMISSIONS: PermissionType<PayrollPdfImport> = {
  "staff/payroll-pdf-imports": {
    index: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
  },
};

const PAYMENT_INFOS_PERMISSIONS: PermissionType<StaffPaymentInfoRef> = {
  "staff/payment-infos": {
    index: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
  },
};

const PAYMENT_REPORTS_PERMISSIONS: PermissionType<StaffPaymentInfoRef> = {
  "staff/payment-reports": {
    index: ({ currentUser, currentCustomer }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") && isRoot(currentUser),
    financeReports: ({ currentUser }, _resource) => ["2", "3", "2070", "1672"].includes(currentUser.id),
  },
};

const PROFILE_PERMISSIONS: PermissionType<null> = {
  "staff/profile": {
    "user-specific-start-project": ({ currentUser }) => userFeatureEnabled(currentUser, "user_specific_start_project"),
  },
};

const STAFF_PERMISSIONS: PermissionType<null> = {
  staff: {
    index: (session) =>
      may(session, "staff/worktimes") ||
      may(session, "staff/timesheets") ||
      may(session, "staff/employees") ||
      may(session, "staff/file-types") ||
      may(session, "staff/vacation_requests") ||
      may(session, "staff/presettlements") ||
      may(session, "staff/settlements") ||
      may(session, "staff/company-news") ||
      may(session, "staff/coachings") ||
      may(session, "staff/coupons") ||
      may(session, "staff/performance") ||
      may(session, "staff/change-requests") ||
      may(session, "staff/worktimes-customer-review") ||
      may(session, "staff/productive-hour-goals") ||
      may(session, "staff/sick-notes") ||
      may(session, "staff/late-notes") ||
      may(session, "staff/payroll-pdf-imports") ||
      may(session, "staff/payment-infos") ||
      may(session, "staff/payment-reports") ||
      may(session, "staff/profile"),

    supervisor: ({ currentCustomer, currentUser }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      userFeatureEnabled(currentUser, "worktimes/supervisor"),

    controlling: ({ currentCustomer, currentUser }) =>
      featureEnabled(currentUser, currentCustomer, "worktimes") &&
      userFeatureEnabled(currentUser, "worktimes/supervisor"),
  },
};

const StaffPermissions = {
  ...WORKTIME_PERMISSIONS,
  ...WORKTIME_CHANGE_REQUEST_PERMISSION,
  ...TIMESHEET_PERMISSIONS,
  ...EMPLOYEE_PERMISSIONS,
  ...FILE_TYPES_PERMISSIONS,
  ...CONTRACTS_PERMISSIONS,
  ...STAFF_HISTORIES_PERMISSIONS,
  ...FILES_PERMISSIONS,
  ...VACATION_PERMISSIONS,
  ...PRESETTLEMENT_PERMISSIONS,
  ...SETTLEMENT_PERMISSIONS,
  ...RESIDUAL_LEAVES_PERMISSIONS,
  ...STAFF_PERMISSIONS,
  ...COMPANY_NEWS_PREMISSIONS,
  ...COACHING_PREMISSIONS,
  ...COUPON_PERMISSIONS,
  ...PERFORMANCE_PERMISSIONS,
  ...CHANGE_REQUEST_PERMISSIONS,
  ...WORKTIME_REVIEW_PERMISSIONS,
  ...PRODUCTIVE_HOUR_GOAL_PERMISSIONS,
  ...SICK_NOTE_PERMISSIONS,
  ...LATE_NOTE_PERMISSIONS,
  ...PAYROLL_PDF_IMPORT_PERMISSIONS,
  ...PAYMENT_INFOS_PERMISSIONS,
  ...PAYMENT_REPORTS_PERMISSIONS,
  ...PROFILE_PERMISSIONS,
};

export default StaffPermissions;
